import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

const BuenosAires = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('areas');

  const content = {
    areas: {
      title: t('buenosAires.areas.title'),
      description: t('buenosAires.areas.description'),
      items: [
        {
          name: t('buenosAires.areas.belgrano.name'),
          description: t('buenosAires.areas.belgrano.description'),
          highlights: t('buenosAires.areas.belgrano.highlights')
        },
        {
          name: t('buenosAires.areas.palermo.name'),
          description: t('buenosAires.areas.palermo.description'),
          highlights: t('buenosAires.areas.palermo.highlights')
        },
        {
          name: t('buenosAires.areas.recoleta.name'),
          description: t('buenosAires.areas.recoleta.description'),
          highlights: t('buenosAires.areas.recoleta.highlights')
        },
        {
          name: t('buenosAires.areas.puertoMadero.name'),
          description: t('buenosAires.areas.puertoMadero.description'),
          highlights: t('buenosAires.areas.puertoMadero.highlights')
        }
      ]
    },
    points: {
      title: t('buenosAires.points.title'),
      description: t('buenosAires.points.description'),
      items: [
        {
          name: t('buenosAires.points.obelisco.name'),
          description: t('buenosAires.points.obelisco.description'),
          type: 'landmark'
        },
        {
          name: t('buenosAires.points.casaRosada.name'),
          description: t('buenosAires.points.casaRosada.description'),
          type: 'landmark'
        },
        {
          name: t('buenosAires.points.teatroColon.name'),
          description: t('buenosAires.points.teatroColon.description'),
          type: 'cultural'
        },
        {
          name: t('buenosAires.points.caminito.name'),
          description: t('buenosAires.points.caminito.description'),
          type: 'cultural'
        },
        {
          name: t('buenosAires.points.rosedal.name'),
          description: t('buenosAires.points.rosedal.description'),
          type: 'landmark'
        },
        {
          name: t('buenosAires.points.sanTelmo.name'),
          description: t('buenosAires.points.sanTelmo.description'),
          highlights: t('buenosAires.points.sanTelmo.highlights'),
          type: 'landmark'
        },
        {
          name: t('buenosAires.points.puertoMadero.name'),
          description: t('buenosAires.points.puertoMadero.description'),
          highlights: t('buenosAires.points.puertoMadero.highlights'),
          type: 'landmark'
        }
      ]
    },
    mustHave: {
      title: t('buenosAires.mustHave.title'),
      description: t('buenosAires.mustHave.description'),
      items: [
        {
          name: t('buenosAires.mustHave.tango.name'),
          description: t('buenosAires.mustHave.tango.description'),
          icon: 'fas fa-music'
        },
        {
          name: t('buenosAires.mustHave.asado.name'),
          description: t('buenosAires.mustHave.asado.description'),
          icon: 'fas fa-fire'
        },
        {
          id: 'bus',
          name: t('buenosAires.mustHave.bus.name'),
          description: t('buenosAires.mustHave.bus.description'),
          icon: 'fas fa-bus'
        },
        {
          name: t('buenosAires.mustHave.medialunas.name'),
          description: t('buenosAires.mustHave.medialunas.description'),
          icon: 'fas fa-coffee'
        }
      ]
    }
  };

  const tabVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      landmark: 'var(--primary-color)',
      cultural: 'var(--dusty-blue)',
      parrilla: '#d2691e',
      'fine-dining': '#8b4513',
      cafe: '#cd853f'
    };
    return colors[type] || 'var(--primary-color)';
  };

  const tabs = [
    { 
      id: 'areas', 
      icon: 'fas fa-bed', 
      label: t('buenosAires.tabs.areas'),
      shortLabel: t('buenosAires.tabs.areas').split(' ')[0] // First word for mobile
    },
    { 
      id: 'points', 
      icon: 'fas fa-map-marker-alt', 
      label: t('buenosAires.tabs.points'),
      shortLabel: t('buenosAires.tabs.points').split(' ')[0]
    },
    { 
      id: 'mustHave', 
      icon: 'fas fa-star', 
      label: t('buenosAires.tabs.mustHave'),
      shortLabel: t('buenosAires.tabs.mustHave').split(' ')[0]
    }
  ];
  return (
    <section id="buenos-aires" className="py-5">
      <div className="container">
        <motion.div 
          className="text-center "
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title">{t('buenosAires.title')}<div className="title-decoration"><img src="/images/section-title.png" alt="Decorative element" /></div></h2>
          <p className="lead">{t('buenosAires.subtitle')}</p>
        </motion.div>

          {/* Content Side */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* Tab Navigation */}
              <div className="buenos-aires-tabs mb-4">
                <nav>
                  <div className="nav nav-pills justify-content-center">
                    {tabs.map((tab) => (
                      <motion.button
                        key={tab.id}
                        className={`nav-link tab-button ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="tab-content-wrapper">
                          <motion.div
                            className="tab-icon"
                            animate={{
                              scale: activeTab === tab.id ? 0 : 1,
                              opacity: activeTab === tab.id ? 0 : 1
                            }}
                            transition={{ duration: 0.3 }}
                          >
                            <i className={tab.icon}></i>
                          </motion.div>
                          
                          <motion.div
                            className="tab-text"
                            animate={{
                              scale: activeTab === tab.id ? 1 : 0,
                              opacity: activeTab === tab.id ? 1 : 0
                            }}
                            transition={{ duration: 0.3 }}
                          >
                            <span className="tab-label-full d-none d-md-inline">{tab.label}</span>
                            <span className="tab-label-short d-md-none">{tab.shortLabel}</span>
                          </motion.div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </nav>
              </div>

              <div className="row">
                <div className="col-lg-6">
                  {/* Tab Content */}
                <motion.div
                  key={activeTab}
                  variants={tabVariants}
                  initial="hidden"
                  animate="visible"
                  className="tab-content"
                >
                  <div className="tab-pane active">
                    <h4 className="mb-2">{content[activeTab].title}</h4>
                    <p className="text-muted mb-3">{content[activeTab].description}</p>
                    
                    <div className="content-items">
                      {content[activeTab].items.map((item, index) => (
                        <motion.div
                          key={index}
                          variants={itemVariants}
                          className="content-item mb-4"
                          data-type={item.type}
                        >
                          <div className="card border-0 shadow-sm">
                            <div className="card-body">
                              <div className="d-flex align-items-start">
                                {item.icon && (
                                  <div className="item-icon me-3">
                                    <i className={`${item.icon} fa-lg`} style={{ color: getTypeColor(item.type) }}></i>
                                  </div>
                                )}
                                <div className="flex-grow-1">
                                  <h5 className="card-title mb-2">
                                    {item.name}
                                    {item.price && (
                                      <span className="badge bg-secondary ms-2">{item.price}</span>
                                    )}
                                  </h5>
                                  <p className="card-text text-muted mb-2">{item.description}</p>
                                  {item.highlights && (
                                    <small className="text-primary">
                                      <i className="fas fa-lightbulb me-1"></i>
                                      {item.highlights}
                                    </small>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
                </div>
                <div className="col-lg-6">
{/* Map Side */}
<motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="map-container"
              >
                  <div className="map-wrapper">
                    <iframe
                      src="https://www.google.com/maps/d/embed?mid=1fTUhCg_Y_JXxmnOMvRdUf2CqQD98Ts0&ehbc=2E312F&noprof=1"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Buenos Aires Map"
                    ></iframe>
                  </div>
                </motion.div>
                </div>
                
                
              </div>
            </motion.div>
          </div>

          
      </div>
    </section>
  );
};

export default BuenosAires;
